import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, FileText, Loader2, Plane, Printer, RefreshCw, Truck, Globe2, XCircle } from "lucide-react";
import api, { inr, fmtDateTime, fmtErr } from "../../lib/api";
import ShareTrackingActions from "../../components/ShareTrackingActions";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STAGES = [
  "INTL_ORDER_CREATED", "INTL_BOOKING_CONFIRMED", "FORWARDING_CREATED", "PICKED_UP",
  "IN_TRANSIT_TO_INDIAFAST", "RECEIVED_AT_FORWARDING_CENTER", "FORWARDING_CENTER_ACCEPTED",
  "PREPARING_INTL_DISPATCH", "INTL_DISPATCHED", "INTL_IN_TRANSIT", "CUSTOMS_CLEARANCE",
  "OUT_FOR_DELIVERY", "DELIVERED",
];
const STAGE_LABELS = {
  INTL_ORDER_CREATED: "International order created",
  INTL_BOOKING_CONFIRMED: "International booking confirmed",
  FORWARDING_CREATED: "Domestic forwarding booked",
  FORWARDING_FAILED: "Forwarding booking failed — retrying",
  PICKED_UP: "Picked up",
  IN_TRANSIT_TO_INDIAFAST: "In transit to IndiaFast",
  RECEIVED_AT_FORWARDING_CENTER: "Awaiting IndiaFast receipt",
  FORWARDING_CENTER_ACCEPTED: "Received at IndiaFast center",
  PREPARING_INTL_DISPATCH: "Preparing international dispatch",
  INTL_DISPATCHED: "International dispatch",
  INTL_IN_TRANSIT: "International in transit",
  CUSTOMS_CLEARANCE: "Customs",
  OUT_FOR_DELIVERY: "Out for delivery",
  DELIVERED: "Delivered",
};

function Flag({ code, className = "h-5 w-7" }) {
  return <img src={`https://flagcdn.com/w80/${code}.png`} alt={code} className={`${className} rounded object-cover`} loading="lazy" />;
}

export default function InternationalDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [acting, setActing] = useState(false);

  const load = () => api.get(`/intl/shipments/${id}`).then(({ data }) => setS(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelShipment = async () => {
    if (!window.confirm("Cancel this international shipment? The domestic forwarding booking will also be cancelled with the courier and the full amount refunded to your wallet.")) return;
    setActing(true);
    try {
      const { data } = await api.post(`/intl/shipments/${id}/cancel`);
      toast.success(`Cancelled — ${inr(data.refunded)} refunded to wallet`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setActing(false); }
  };

  const retryForwarding = async () => {
    setActing(true);
    try {
      await api.post(`/intl/shipments/${id}/retry-forwarding`);
      toast.success("Domestic forwarding booked");
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setActing(false); }
  };

  if (!s) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const doneStages = new Set([...(s.tracking_events || []).map((e) => e.status), ...(s.derived_stages || [])]);
  if (s.status === "FORWARDING_FAILED") doneStages.add("INTL_BOOKING_CONFIRMED");
  const currentIdx = STAGES.reduce((acc, st, i) => (doneStages.has(st) || (s.derived_stages || []).includes(st) ? i : acc), -1);

  return (
    <div className="mx-auto max-w-5xl" data-testid="intl-detail-page">
      <Link to="/app/international" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-shipany-blue" data-testid="intl-detail-back">
        <ArrowLeft className="h-4 w-4" /> International shipments
      </Link>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Flag code="in" /> <Plane className="h-4 w-4 text-shipany-blue" /> <Flag code={(s.destination_country || "").toLowerCase()} />
            <div>
              <h1 className="font-display text-xl font-bold text-shipany-navy" data-testid="intl-detail-number">{s.shipment_number}</h1>
              <p className="text-xs text-slate-400">India → {s.destination_country_name} · {s.courier_name} {s.service} · {s.transit_days}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ShareTrackingActions code={s.international_awb || s.shipment_number} label="international shipment" testId="share-intl-detail" />
            <span className="rounded-full bg-shipany-blue/10 px-3 py-1.5 text-xs font-bold text-shipany-blue" data-testid="intl-detail-status">{s.stage_label}</span>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-4">
          {[["Billed Weight", `${s.total_billed_weight} kg`], ["Boxes", s.total_boxes], ["Shipment Value", inr(s.shipment_value)],
            [s.payment_status === "settled" ? "Final Charged" : s.payment_mode === "cash" ? "Estimated · Cash" : "Amount Paid", inr(s.final_amount ?? s.customer_amount)]].map(([l, v], i) => (
            <div key={l} className={`rounded-xl p-4 text-center ${i === 3 ? "bg-shipany-blue/5 ring-1 ring-shipany-blue/20" : "bg-slate-50"}`}>
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{l}</p>
              <p className="mono mt-1 text-lg font-bold text-shipany-navy" data-testid={`detail-${l.replace(/\s/g, "-").toLowerCase()}`}>{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-400">International AWB (IndiaFast)</p>
            <p className="mono mt-1 font-bold text-shipany-navy" data-testid="detail-intl-awb">{s.international_awb}</p>
          </div>
          <div className="rounded-xl bg-slate-50 p-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-400">Domestic Forwarding AWB</p>
            {s.forwarding ? (
              <p className="mono mt-1 font-bold text-shipany-navy" data-testid="detail-dom-awb">{s.forwarding.awb} <span className="ml-1 text-xs font-normal text-slate-400">via {s.forwarding.courier_name} · your charge ₹0</span></p>
            ) : (
              <p className="mt-1 text-xs font-medium text-amber-600" data-testid="detail-dom-pending">{s.forwarding_status === "failed" ? "Forwarding booking failed — our team is retrying" : "Forwarding booking in progress"}</p>
            )}
          </div>
        </div>

        <div className="mt-6" data-testid="intl-timeline">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Timeline</p>
          <div className="mt-3 space-y-0">
            {STAGES.map((st, i) => {
              const done = i <= currentIdx;
              const current = i === currentIdx + 1;
              return (
                <div key={st} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${done ? "bg-emerald-500 text-white" : current ? "bg-shipany-blue text-white pulse-dot" : "bg-slate-200"}`}>
                      {done && <Check className="h-3 w-3" />}
                    </span>
                    {i < STAGES.length - 1 && <span className={`h-5 w-0.5 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />}
                  </div>
                  <p className={`pt-0.5 text-sm ${done ? "font-semibold text-shipany-navy" : "text-slate-400"}`}>{STAGE_LABELS[st]}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6" data-testid="intl-boxes-table">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Boxes & contents</p>
          <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
                <th className="px-4 py-2.5">Box ID</th><th className="px-4 py-2.5">Box</th><th className="px-4 py-2.5">Weight</th><th className="px-4 py-2.5">Dims (cm)</th><th className="px-4 py-2.5">Billed</th><th className="px-4 py-2.5">Items</th>
              </tr></thead>
              <tbody>
                {s.boxes.map((b) => (
                  <tr key={b.box_id} className="border-t border-slate-100">
                    <td className="mono px-4 py-2.5 text-xs font-semibold text-shipany-blue">{b.box_id}</td>
                    <td className="px-4 py-2.5">{b.package_number} / {s.total_boxes}</td>
                    <td className="mono px-4 py-2.5">{b.weight} kg</td>
                    <td className="mono px-4 py-2.5">{b.length}×{b.width}×{b.height}</td>
                    <td className="mono px-4 py-2.5 font-semibold">{b.billed_weight} kg</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{(b.items || []).map((it) => `${it.name} ×${it.quantity}`).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-slate-50 p-4" data-testid="intl-billing-breakup">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Billing</p>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">International Shipping ({s.courier_name}){s.payment_mode === "cash" && s.payment_status !== "settled" ? " — estimate" : ""}</span><span className="mono font-semibold">{inr(s.customer_amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Domestic Forwarding</span><span className="mono font-semibold text-emerald-600">₹0</span></div>
            {s.payment_status === "settled" && Math.abs(s.bill_adjustment || 0) > 0.004 && (
              <div className="flex justify-between" data-testid="billing-adjustment-row"><span className="text-slate-500">Final bill adjustment (warehouse verified)</span><span className={`mono font-semibold ${s.bill_adjustment > 0 ? "text-amber-600" : "text-emerald-600"}`}>{s.bill_adjustment > 0 ? "+" : "−"}{inr(Math.abs(s.bill_adjustment))}</span></div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-2 font-bold">
              <span>{s.payment_status === "settled" ? "Total Charged" : s.payment_mode === "cash" ? "Estimated Total — not charged yet" : "Total Paid"}</span>
              <span className="mono" data-testid="billing-total">{inr(s.final_amount ?? s.customer_amount)}</span>
            </div>
            {s.payment_mode === "cash" && s.payment_status !== "settled" && s.status !== "CANCELLED" && (
              <p className="pt-1 text-[11px] font-medium text-amber-600" data-testid="billing-cash-note">Cash booking — ₹0 charged at booking. The final bill will be settled from your wallet after warehouse verification.</p>
            )}
          </div>
        </div>

        {s.status === "CANCELLED" && (
          <div className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-600" data-testid="cancelled-banner">
            {["paid", "settled"].includes(s.payment_status || "paid")
              ? "This international shipment was cancelled — the full amount was refunded to your wallet."
              : "This international shipment was cancelled — no payment had been collected."}
          </div>
        )}
        {s.status !== "CANCELLED" && !["INTL_DISPATCHED", "INTL_IN_TRANSIT", "CUSTOMS_CLEARANCE", "OUT_FOR_DELIVERY", "DELIVERED"].includes(s.status) && (
          <div className="mt-6 flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="intl-actions">
            {(s.forwarding_status === "failed" || s.forwarding_status === "pending") && (
              <button onClick={retryForwarding} disabled={acting} data-testid="retry-forwarding-btn"
                className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-50">
                {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Retry Forwarding Booking
              </button>
            )}
            <button onClick={cancelShipment} disabled={acting} data-testid="cancel-intl-btn"
              className="flex items-center gap-2 rounded-xl border border-red-300 px-5 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50">
              <XCircle className="h-4 w-4" /> {s.payment_mode === "cash" && s.payment_status !== "settled" ? "Cancel Shipment (nothing charged yet)" : `Cancel Shipment (refund ${inr(s.final_amount ?? s.customer_amount)})`}
            </button>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={`${API_BASE}/api/intl/shipments/${s.shipment_id}/label?type=domestic`} target="_blank" rel="noreferrer" data-testid="detail-label-domestic"
            className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3]"><Truck className="h-4 w-4" /> Domestic Forwarding Label</a>
          <a href={`${API_BASE}/api/intl/shipments/${s.shipment_id}/label?type=international`} target="_blank" rel="noreferrer" data-testid="detail-label-intl"
            className="flex items-center gap-2 rounded-xl bg-shipany-navy px-5 py-2.5 text-sm font-bold text-white"><Globe2 className="h-4 w-4" /> IndiaFast International Label</a>
          <a href={`${API_BASE}/api/intl/shipments/${s.shipment_id}/label?type=both`} target="_blank" rel="noreferrer" data-testid="detail-label-both"
            className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><Printer className="h-4 w-4" /> Print Both Labels</a>
          <a href={`${API_BASE}/api/shipments/${s.shipment_id}/document?type=invoice`} target="_blank" rel="noreferrer" data-testid="detail-invoice-btn"
            className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><FileText className="h-4 w-4" /> Invoice</a>
          <a href={`${API_BASE}/api/intl/shipments/${s.shipment_id}/label?type=both`} download data-testid="detail-label-all"
            className="flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-4 w-4" /> Download All Labels</a>
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Paste BOTH labels on the package: the domestic label gets it to the IndiaFast center, the international label travels with it to the final customer.</p>
        <p className="mt-1 text-[11px] text-slate-400">Created {fmtDateTime(s.created_at)}</p>
      </div>
    </div>
  );
}
